import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { validateAndSanitizeText } from '../utils/sanitization';

const DecisionLogComponent = () => {
  const { decisionEntries, saveDecisionEntry } = useAuth();
  const [newDecision, setNewDecision] = useState({
    title: '',
    context: '',
    decision: '',
    reasoning: '',
    alternatives: '',
    consequences: ''
  });
  const [isAddingDecision, setIsAddingDecision] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newDecision.title.trim() && newDecision.decision.trim()) {
      // Sanitize all inputs before saving
      const decision = {
        id: Date.now().toString(),
        title: validateAndSanitizeText(newDecision.title.trim(), 500),
        context: validateAndSanitizeText(newDecision.context.trim(), 5000),
        decision: validateAndSanitizeText(newDecision.decision.trim(), 5000),
        reasoning: validateAndSanitizeText(newDecision.reasoning.trim(), 5000),
        alternatives: validateAndSanitizeText(newDecision.alternatives.trim(), 5000),
        consequences: validateAndSanitizeText(newDecision.consequences.trim(), 5000),
        timestamp: new Date().toISOString(),
        dateCreated: new Date().toISOString().split('T')[0]
      };

      await saveDecisionEntry(decision);

      setNewDecision({
        title: '',
        context: '',
        decision: '',
        reasoning: '',
        alternatives: '',
        consequences: ''
      });
      setIsAddingDecision(false);
    }
  };

  const sortedDecisions = [...decisionEntries].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div className="decision-log-container">
      <div className="decision-log-header">
        <h2>📋 Decision Log</h2>
        <button
          className="decision-log-add-button"
          onClick={() => setIsAddingDecision(!isAddingDecision)}
        >
          {isAddingDecision ? 'Cancel' : 'Log Decision'}
        </button>
      </div>

      {isAddingDecision && (
        <form className="decision-log-form" onSubmit={handleSubmit}>
          <div className="decision-log-form-group">
            <label htmlFor="title">Decision Title *:</label>
            <input
              type="text"
              id="title"
              placeholder="Brief title describing the decision..."
              value={newDecision.title}
              onChange={(e) => setNewDecision({...newDecision, title: e.target.value})}
              required
            />
          </div>

          <div className="decision-log-form-group">
            <label htmlFor="context">Context/Background:</label>
            <textarea
              id="context"
              rows="3"
              placeholder="What led to this decision? What was the situation?"
              value={newDecision.context}
              onChange={(e) => setNewDecision({...newDecision, context: e.target.value})}
            />
          </div>

          <div className="decision-log-form-group">
            <label htmlFor="decision">Decision Made *:</label>
            <textarea
              id="decision"
              rows="3"
              placeholder="What decision was made?"
              value={newDecision.decision}
              onChange={(e) => setNewDecision({...newDecision, decision: e.target.value})}
              required
            />
          </div>

          <div className="decision-log-form-group">
            <label htmlFor="reasoning">Reasoning:</label>
            <textarea
              id="reasoning"
              rows="3"
              placeholder="Why was this decision made? What factors were considered?"
              value={newDecision.reasoning}
              onChange={(e) => setNewDecision({...newDecision, reasoning: e.target.value})}
            />
          </div>

          <div className="decision-log-form-group">
            <label htmlFor="alternatives">Alternatives Considered:</label>
            <textarea
              id="alternatives"
              rows="3"
              placeholder="What other options were considered?"
              value={newDecision.alternatives}
              onChange={(e) => setNewDecision({...newDecision, alternatives: e.target.value})}
            />
          </div>

          <div className="decision-log-form-group">
            <label htmlFor="consequences">Expected Consequences/Impact:</label>
            <textarea
              id="consequences"
              rows="3"
              placeholder="What are the expected outcomes or impacts of this decision?"
              value={newDecision.consequences}
              onChange={(e) => setNewDecision({...newDecision, consequences: e.target.value})}
            />
          </div>

          <div className="decision-log-form-actions">
            <button type="submit" className="decision-log-save-button">
              Save Decision
            </button>
          </div>
        </form>
      )}

      <div className="decision-log-entries">
        {sortedDecisions.length === 0 ? (
          <div className="decision-log-empty">
            <p>No decisions logged yet. Start tracking important decisions for your agricultural planning!</p>
          </div>
        ) : (
          sortedDecisions.map((decision) => (
            <div key={decision.id} className="decision-log-entry">
              <div className="decision-log-entry-header">
                <h3 className="decision-log-entry-title">{decision.title}</h3>
                <span className="decision-log-entry-date">{decision.dateCreated}</span>
              </div>

              {decision.context && (
                <div className="decision-log-entry-section">
                  <h4>Context</h4>
                  <p>{decision.context}</p>
                </div>
              )}

              <div className="decision-log-entry-section">
                <h4>Decision</h4>
                <p>{decision.decision}</p>
              </div>

              {decision.reasoning && (
                <div className="decision-log-entry-section">
                  <h4>Reasoning</h4>
                  <p>{decision.reasoning}</p>
                </div>
              )}

              {decision.alternatives && (
                <div className="decision-log-entry-section">
                  <h4>Alternatives Considered</h4>
                  <p>{decision.alternatives}</p>
                </div>
              )}

              {decision.consequences && (
                <div className="decision-log-entry-section">
                  <h4>Expected Consequences</h4>
                  <p>{decision.consequences}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DecisionLogComponent;